import {
  editOffer,
  generateSdpFragment,
  parseOffer,
  supportsNonAdvertisedCodec,
} from '../../VideoStream/utils';
import { beeXLogger } from '../../../helpers/logger';

let globalCodecsCache = null;
let globalCodecsPromise = null;

export async function getGlobalCodecs() {
  if (globalCodecsCache !== null) {
    return globalCodecsCache;
  }

  if (globalCodecsPromise !== null) {
    return globalCodecsPromise;
  }

  globalCodecsPromise = Promise.all(
    [
      ['pcma/8000/2'],
      [
        'multiopus/48000/6',
        'channel_mapping=0,4,1,2,3,5;num_streams=4;coupled_streams=2',
      ],
      ['L16/48000/2'],
    ].map((c) =>
      supportsNonAdvertisedCodec(c[0], c[1]).then((r) => (r ? c[0] : false)),
    ),
  )
    .then((codecs) => {
      const filtered = codecs.filter((e) => e !== false);
      globalCodecsCache = filtered;
      globalCodecsPromise = null;
      beeXLogger.debug('Supported codecs:', filtered);
      return filtered;
    })
    .catch((err) => {
      beeXLogger.error('Error checking codecs:', err);
      globalCodecsPromise = null;
      return [];
    });

  return globalCodecsPromise;
}

export class RTCCodecAdapter {
  peerConnection = null;
  sessionUrl = '';
  offerData = null;
  queuedCandidates = [];
  restartTimeout = null;
  renewStreamTimeout = null;
  nonAdvertisedCodecs = [];
  restartCount = 0;
  isConnecting = false;
  isDestroyed = false;
  RETRY_PAUSE = 3000;
  TIMEOUT_RENEW_STREAM = 12000;
  iceCheckingTimeout = null;
  ICE_CHECKING_LIMIT = 3000;
  connectionEstablished = false;
  disconnectTimeout = null;
  DISCONNECT_GRACE_PERIOD = 2000;

  async initializeCodecs() {
    return await getGlobalCodecs();
  }

  clearIceCheckingTimeout() {
    if (this.iceCheckingTimeout) {
      clearTimeout(this.iceCheckingTimeout);
      this.iceCheckingTimeout = null;
    }
  }

  clearDisconnectTimeout() {
    if (this.disconnectTimeout) {
      clearTimeout(this.disconnectTimeout);
      this.disconnectTimeout = null;
    }
  }

  cleanupPeerConnection() {
    if (!this.peerConnection) return;

    this.peerConnection.onicecandidate = null;
    this.peerConnection.ontrack = null;
    this.peerConnection.oniceconnectionstatechange = null;
    this.peerConnection.onconnectionstatechange = null;
    this.clearIceCheckingTimeout();
    this.clearDisconnectTimeout();

    try {
      this.peerConnection.close();
    } catch (e) {
      beeXLogger.warn('peer close failed:', e);
    }

    this.peerConnection = null;
    this.connectionEstablished = false;
  }

  handleError = (
    err,
    stream,
    config,
    onSuccess,
    onError,
    oniceconnectionstatechange,
  ) => {
    beeXLogger.error('WebRTC ERROR ======>', err);
    if (this.isDestroyed) {
      return;
    }

    this.connectionEstablished = false;

    if (onError && this.restartCount > 5) {
      this.isConnecting = false;
      onError(err);
      return;
    }

    if (this.restartTimeout) return;

    this.cleanupPeerConnection();

    if (this.sessionUrl) {
      fetch(this.sessionUrl, { method: 'DELETE' }).catch(() => {});
    }

    this.sessionUrl = '';
    this.queuedCandidates = [];

    if (this.restartCount < 6) {
      this.restartCount++;

      this.restartTimeout = window.setTimeout(() => {
        beeXLogger.debug('RETRY LOCAL');
        this.restartTimeout = null;
        this.isConnecting = false;
        this.connect(
          stream,
          config,
          onSuccess,
          onError,
          oniceconnectionstatechange,
        );
      }, this.RETRY_PAUSE);
    } else {
      this.isConnecting = false;
      this.destroy();
    }
  };

  sendLocalCandidates = async (candidates, config) => {
    if (!this.sessionUrl || !this.offerData || !config.streamToken) return;
    // beeXLogger.debug("SESSION URL: ", this.sessionUrl);

    const res = await fetch(this.sessionUrl, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/trickle-ice-sdpfrag',
        'If-Match': '*',
        Authorization: `BeeX a:${config.streamToken}`,
      },
      body: generateSdpFragment(this.offerData, candidates),
    });

    if (res.status === 204) return;
    if (res.status === 404) throw new Error('stream not found');

    throw new Error(`bad status code ${res.status}`);
  };

  startTimeoutRenewStream = (callback) => {
    this.clearTimeoutRenewStream();
    this.renewStreamTimeout = window.setTimeout(() => {
      this.renewStreamTimeout = null;
      callback();
    }, this.TIMEOUT_RENEW_STREAM);
  };

  clearTimeoutRenewStream = () => {
    if (this.renewStreamTimeout) {
      clearTimeout(this.renewStreamTimeout);
      this.renewStreamTimeout = null;
    }
  };

  async connect(
    stream,
    config,
    onSuccess,
    onError,
    oniceconnectionstatechange,
  ) {
    if (this.isConnecting || this.isDestroyed) {
      return;
    }

    this.isConnecting = true;
    this.startTimeoutRenewStream(() => {
      if (onError) {
        onError(new Error('Connection timeout'));
      }
    });

    try {
      console.log('CONNECTING...', stream);

      this.nonAdvertisedCodecs = await this.initializeCodecs();

      if (this.isDestroyed) {
        this.isConnecting = false;
        return;
      }

      this.cleanupPeerConnection();
      beeXLogger.debug('connect - playback local, RTCPeerConnection');

      this.peerConnection = new RTCPeerConnection({
        iceServers: [
          { urls: 'stun:turn01.beex.vn:3478' },
          {
            urls: [
              'turn:stun01.beex.vn:3478?transport=udp',
              'turn:stun01.beex.vn:3478?transport=tcp',
              'turns:stun01.beex.vn:5349?transport=tcp',
            ],
            username: 'beex',
            credential: '82zu0TPZFTXp6qau',
          },
          {
            urls: 'turn:numb.viagenie.ca',
            username: 'webrtc@live.com',
            credential: 'muazkh',
          },
        ],
        iceCandidatePoolSize: 10,
      });

      this.peerConnection.addTransceiver('video', { direction: 'sendrecv' });
      this.peerConnection.addTransceiver('audio', { direction: 'sendrecv' });

      this.peerConnection.onicecandidate = (evt) => {
        if (!evt.candidate || this.restartTimeout || this.isDestroyed) return;

        if (!this.sessionUrl) {
          this.queuedCandidates.push(evt.candidate);
        } else {
          this.sendLocalCandidates([evt.candidate], config).catch((err) =>
            this.handleError(
              err,
              stream,
              config,
              onSuccess,
              onError,
              oniceconnectionstatechange,
            ),
          );
        }
      };

      this.peerConnection.oniceconnectionstatechange = () => {
        if (this.isDestroyed) return;

        const state = this.peerConnection.iceConnectionState;
        oniceconnectionstatechange(state);

        beeXLogger.debug('ICE STATE:', state);

        if (state === 'connected' || state === 'completed') {
          beeXLogger.debug('ICE reconnected, clearing disconnect timeout');
          this.clearDisconnectTimeout();
          this.connectionEstablished = true;
          return;
        }

        if (state === 'checking') {
          beeXLogger.debug('ICE checking phase...');
          return;
        }

        if (
          (state === 'disconnected' || state === 'failed') &&
          this.connectionEstablished
        ) {
          beeXLogger.debug(
            `ICE ${state} after connection established, scheduling retry with grace period`,
          );

          this.clearDisconnectTimeout();

          this.disconnectTimeout = window.setTimeout(() => {
            if (
              !this.isDestroyed &&
              this.peerConnection?.iceConnectionState === state
            ) {
              beeXLogger.debug(
                `ICE still ${state} after grace period, triggering retry`,
              );
              this.handleError(
                new Error(`ICE ${state}`),
                stream,
                config,
                onSuccess,
                onError,
                oniceconnectionstatechange,
              );
            } else {
              beeXLogger.debug(
                'ICE recovered during grace period or connection closed',
              );
            }
          }, this.DISCONNECT_GRACE_PERIOD);
        }
      };
      beeXLogger.debug('this.isDestroyed: ', this.isDestroyed);

      this.peerConnection.ontrack = (evt) => {
        if (this.isDestroyed) return;

        beeXLogger.debug('TRACK RECEIVED - Connection established');
        this.connectionEstablished = true;
        this.clearDisconnectTimeout();
        this.clearTimeoutRenewStream();
        onSuccess(evt.streams[0], this.peerConnection?.iceConnectionState);
      };

      const offer = await this.peerConnection.createOffer();

      if (this.isDestroyed) {
        this.isConnecting = false;
        return;
      }

      offer.sdp = editOffer(offer.sdp, this.nonAdvertisedCodecs);
      this.offerData = parseOffer(offer.sdp);

      await this.peerConnection.setLocalDescription(offer);

      if (this.isDestroyed) {
        this.isConnecting = false;
        return;
      }

      const base = `https://${stream.wsHost}:${stream.wssPort}`;
      beeXLogger.log(
        'HTTP Link: ',
        `https://${stream?.wsHost}:${stream?.hlsPort}${stream?.sourcePath || stream?.path || `/${stream?.source}`}/index.m3u8?token=${config.streamToken}`,
      );
      beeXLogger.log(
        'rtsp: ',
        `rtsp://${stream.serial}:${config.streamToken}@${stream.wsHost}:${stream.rtspPort || '10554'}${stream?.sourcePath ? stream?.sourcePath : stream.path || stream?.source}`,
      );

      const response = await fetch(
        `${base}${stream?.sourcePath ? stream?.sourcePath : stream.path || stream?.source}/whep`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/sdp',
            Authorization: `BeeX a:${config.streamToken}`,
          },
          body: offer.sdp,
        },
      );

      if (this.isDestroyed) {
        this.isConnecting = false;
        return;
      }

      if (response.status !== 201) {
        if (response.status === 404) throw new Error('stream not found');
        if (response.status === 400)
          throw new Error((await response.json()).error);

        throw new Error(`bad status code ${response.status}`);
      }

      this.sessionUrl = `${base}${response.headers.get('location')}`;
      const answerSdp = await response.text();

      if (this.peerConnection.signalingState === 'have-local-offer') {
        await this.peerConnection.setRemoteDescription({
          type: 'answer',
          sdp: answerSdp,
        });
      } else {
        beeXLogger.warn(
          'SKIP ANSWER: state =',
          this.peerConnection.signalingState,
        );
        this.isConnecting = false;
        return;
      }

      if (this.queuedCandidates.length) {
        await this.sendLocalCandidates(this.queuedCandidates, config);
        this.queuedCandidates = [];
      }

      this.restartCount = 0;
      this.isConnecting = false;
    } catch (err) {
      this.handleError(
        err,
        stream,
        config,
        onSuccess,
        onError,
        oniceconnectionstatechange,
      );
    }
  }

  destroy() {
    this.isDestroyed = true;
    this.isConnecting = false;
    this.clearIceCheckingTimeout();
    this.clearDisconnectTimeout();

    this.cleanupPeerConnection();

    if (this.sessionUrl) {
      fetch(this.sessionUrl, { method: 'DELETE' }).catch(console.error);
    }

    if (this.restartTimeout) {
      clearTimeout(this.restartTimeout);
      this.restartTimeout = null;
    }

    this.clearTimeoutRenewStream();

    this.sessionUrl = '';
    this.offerData = null;
    this.queuedCandidates = [];
    this.restartCount = 0;
    this.connectionEstablished = false;
  }
}

export function preloadCodecs() {
  return getGlobalCodecs();
}
